import React from "react";
import LegalLayout from "@/pages/LegalLayout";

const P = ({ children }) => <p>{children}</p>;
const List = ({ items }) => (
  <ul className="space-y-2 pl-0">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3">
        <span className="w-1.5 h-1.5 bg-accent block mt-2.5 shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function TermsOfService() {
  const sections = [
    {
      id: "introduction",
      title: "Introduction",
      body: (
        <>
          <P>
            These Terms of Service ("Terms") govern your use of the PHOS
            Management Inc. website and your interactions with PHOS Management
            Inc. ("PHOS Management," "we," "us," or "our").
          </P>
          <P>
            By accessing our website, submitting information through our
            website, purchasing services, or otherwise using our services, you
            agree to these Terms.
          </P>
        </>
      ),
    },
    {
      id: "our-services",
      title: "Our Services",
      body: (
        <>
          <P>
            PHOS Management Inc. provides marketing, media, creative,
            advertising, production, consulting, business, and related services.
          </P>
          <P>
            Specific services, deliverables, payment terms, timelines, and other
            conditions may be governed by a separate proposal, service
            agreement, statement of work, invoice, or contract.
          </P>
          <P>
            If a separate written agreement conflicts with these Terms regarding
            services purchased by a client, the applicable written agreement
            will control with respect to that conflict.
          </P>
        </>
      ),
    },
    {
      id: "website-use",
      title: "Website Use",
      body: (
        <>
          <P>You agree to use our website only for lawful purposes.</P>
          <P>
            You may not intentionally interfere with the operation or security
            of the website, attempt to gain unauthorized access to systems or
            information, or use the website in a manner that violates applicable
            law.
          </P>
        </>
      ),
    },
    {
      id: "information-submitted",
      title: "Information Submitted to Us",
      body: (
        <>
          <P>You agree that information you provide to us will be accurate to the best of your knowledge.</P>
          <P>
            Submitting a form or providing your contact information does not
            automatically enroll you in marketing SMS communications unless you
            separately provide the applicable consent.
          </P>
        </>
      ),
    },
    {
      id: "sms-terms",
      title: "SMS Messaging Terms",
      body: (
        <>
          <P>
            PHOS Management Inc. may offer SMS messaging programs for
            individuals who expressly consent to receive text messages from us.
          </P>
          <P>Messages may include:</P>
          <List
            items={[
              "Marketing and promotional messages",
              "Special offers or promotions",
              "Service information",
              "Appointment information",
              "Follow-ups regarding inquiries",
              "Customer support communications",
              "Business or account-related updates",
            ]}
          />
          <P>Marketing and non-marketing SMS consent may be collected separately where applicable.</P>
          <List
            items={[
              "Message frequency varies.",
              "Message and data rates may apply.",
              "Consent to receive marketing text messages is not a condition of purchasing goods or services from PHOS Management Inc.",
            ]}
          />
        </>
      ),
    },
    {
      id: "sms-optout",
      title: "SMS Opt-Out",
      body: (
        <>
          <P>You can cancel SMS communications at any time.</P>
          <div className="border-l-2 border-accent pl-5 py-2 bg-white/[0.02]">
            <P>
              Text <strong className="text-ink-50">STOP</strong> in response to
              a message from PHOS Management Inc.
            </P>
          </div>
          <P>
            After you send STOP, we may send you a confirmation message
            confirming that you have been unsubscribed. After that confirmation,
            you will no longer receive SMS messages from that messaging program
            unless you opt in again.
          </P>
          <P>
            If you later wish to receive messages again, you may opt in using
            the same or another available registration method.
          </P>
        </>
      ),
    },
    {
      id: "sms-help",
      title: "SMS Help",
      body: (
        <>
          <P>
            If you experience problems with our messaging program, reply{" "}
            <strong className="text-ink-50">HELP</strong> for assistance.
          </P>
          <P>You may also contact us directly using the information at the bottom of this page.</P>
        </>
      ),
    },
    {
      id: "carrier-disclaimer",
      title: "Carrier Disclaimer",
      body: <P>Carriers are not liable for delayed or undelivered messages.</P>,
    },
    {
      id: "message-charges",
      title: "Message and Data Charges",
      body: (
        <>
          <P>
            Message and data rates may apply to messages sent between you and
            PHOS Management Inc.
          </P>
          <P>Your wireless carrier determines applicable messaging or data charges.</P>
          <P>
            If you have questions regarding your text messaging or data plan,
            contact your wireless provider.
          </P>
        </>
      ),
    },
    {
      id: "privacy",
      title: "Privacy",
      body: (
        <>
          <P>Your privacy is important to us.</P>
          <P>
            Please review our{" "}
            <a href="/privacy-policy" className="text-accent hover:underline">
              Privacy Policy
            </a>{" "}
            for information regarding how we collect, use, and protect personal
            information.
          </P>
          <P>SMS opt-in data and consent will be handled as described in our Privacy Policy.</P>
        </>
      ),
    },
    {
      id: "intellectual-property",
      title: "Intellectual Property",
      body: (
        <>
          <P>
            Unless otherwise stated, content appearing on this website,
            including text, graphics, branding, images, videos, website design,
            and other materials, is owned by or licensed to PHOS Management Inc.
            and may be protected by applicable intellectual property laws.
          </P>
          <P>
            You may not reproduce, distribute, modify, or commercially exploit
            our website content without authorization except where permitted by
            law.
          </P>
        </>
      ),
    },
    {
      id: "third-party",
      title: "Third-Party Services",
      body: (
        <>
          <P>Our website may use or link to services operated by other companies.</P>
          <P>
            PHOS Management Inc. is not responsible for the availability,
            security, content, or practices of websites or services that we do
            not control.
          </P>
        </>
      ),
    },
    {
      id: "disclaimer",
      title: "Disclaimer",
      body: (
        <>
          <P>
            Information provided through our website is intended for general
            informational purposes unless specifically provided as part of a
            professional service engagement.
          </P>
          <P>
            We do not guarantee that every website feature will always be
            available, uninterrupted, or error-free.
          </P>
        </>
      ),
    },
    {
      id: "liability",
      title: "Limitation of Liability",
      body: (
        <>
          <P>
            To the extent permitted by applicable law, PHOS Management Inc. will
            not be liable for indirect, incidental, special, consequential, or
            punitive damages arising solely from your use of or inability to use
            our website.
          </P>
          <P>Nothing in these Terms limits liability where doing so would be prohibited by applicable law.</P>
        </>
      ),
    },
    {
      id: "changes",
      title: "Changes to These Terms",
      body: (
        <>
          <P>We may update these Terms periodically.</P>
          <P>The "Last Updated" date at the top of this page indicates when these Terms were most recently revised.</P>
        </>
      ),
    },
    {
      id: "governing-law",
      title: "Governing Law",
      body: (
        <P>
          These Terms will be governed by applicable laws of the State of
          Michigan and the United States, without regard to conflict-of-law
          principles, except where applicable law requires otherwise.
        </P>
      ),
    },
  ];

  return (
    <LegalLayout
      testid="terms-of-service-page"
      title={["Terms of", "Service."]}
      updated="September 21, 2026"
      sections={sections}
      otherPage={{ to: "/privacy-policy", label: "Privacy Policy" }}
    />
  );
}
